import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductImage from "@/components/ProductImage";
import DropTimer from "@/components/DropTimer";
import Reveal from "@/components/Reveal";

const SIZE_GUIDE = [
  ["XS", "34", "26", "24"],
  ["S", "36", "28", "25"],
  ["M", "38", "30", "26"],
  ["L", "40", "32", "27"],
  ["XL", "42", "34", "28"],
  ["XXL", "44", "36", "29"],
];

function Accordion({ title, children, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button data-testid={testId} onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between py-4 text-left">
        <span className="font-mono text-[11px] tracking-[0.3em] text-bone/80">{title}</span>
        <span className="font-mono text-bone/60">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="pb-5 text-sm font-light leading-relaxed text-neutral-400">{children}</div>}
    </div>
  );
}

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [sizeError, setSizeError] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const { addItem, setDrawerOpen } = useCart();

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        setColor(r.data.colors[0]);
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4" data-testid="product-not-found">
        <p className="font-display text-3xl font-bold uppercase text-bone">Lost?</p>
        <p className="font-mono text-[10px] tracking-[0.3em] text-fade">NO COINCIDENCES — THIS PIECE DOESN'T EXIST</p>
        <Link to="/shop" className="mt-4 border border-bone/40 px-6 py-3 font-mono text-xs tracking-[0.25em] text-bone hover:bg-bone hover:text-ink">
          BACK TO SHOP
        </Link>
      </div>
    );

  if (!product)
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="font-mono text-xs tracking-[0.35em] text-fade">ALIGNING…</p>
      </div>
    );

  const addToCart = () => {
    if (!size) {
      setSizeError(true);
      return;
    }
    addItem(product, size, color);
    setDrawerOpen(true);
  };

  return (
    <div data-testid="product-page" className="pt-16">
      <div className="grid lg:grid-cols-2">
        <div className="border-b border-white/10 lg:border-b-0 lg:border-r">
          <ProductImage product={{ ...product, image_url: product.color_images?.[color] || product.image_url }} ratio="aspect-[3/4]" fit="contain" tag="FRAME 01 — FULL" />
          <div className="grid grid-cols-2 gap-px border-t border-white/10 bg-white/10">
            {[0, 1].map((i) => (
              <ProductImage
                key={i}
                product={{ ...product, image_url: product.gallery?.[i] || "" }}
                ratio="aspect-square"
                tag={i === 0 ? "FRAME 02 — DETAIL" : "FRAME 03 — FABRIC"}
                className="bg-ink"
              />
            ))}
          </div>
        </div>

        <div className="px-5 py-10 sm:px-10 lg:sticky lg:top-16 lg:self-start">
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.35em] text-fade">
              {product.category.toUpperCase()} — ANGEL N°{product.angel} {product.is_new ? "— NEW RELEASE" : ""}
            </p>
            <h1 data-testid="product-name" className="mt-3 font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-bone sm:text-4xl">
              {product.name}
            </h1>
            <p data-testid="product-price" className="mt-3 font-mono text-xl text-bone">
              {fmt(product.price)}
            </p>
            {product.drop_at && <DropTimer dropAt={product.drop_at} label={`${product.name} — FULL RELEASE`} />}
          </Reveal>

          {product.colors.length > 1 && (
            <div className="mt-8">
              <p className="font-mono text-[10px] tracking-[0.3em] text-fade">
                COLOR — <span className="text-bone">{color.toUpperCase()}</span>
              </p>
              <div className="mt-3 flex gap-2">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    data-testid={`color-option-${c.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => setColor(c)}
                    className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors ${color === c ? "border-bone bg-bone text-ink" : "border-white/20 text-bone/70 hover:border-white/50"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[10px] tracking-[0.3em] text-fade">
                SIZE {sizeError && !size ? <span className="text-red-400">— SELECT A SIZE</span> : null}
              </p>
              <button data-testid="size-guide-toggle" onClick={() => setGuideOpen((v) => !v)} className="font-mono text-[10px] tracking-[0.3em] text-bone/60 underline underline-offset-4 hover:text-bone">
                SIZE GUIDE
              </button>
            </div>
            {guideOpen && (
              <div data-testid="size-guide-table" className="mt-4 overflow-x-auto border border-white/15">
                <table className="w-full font-mono text-[10px] tracking-widest text-bone/70">
                  <thead>
                    <tr className="border-b border-white/15 text-fade">
                      <th className="p-2 text-left">SIZE</th>
                      <th className="p-2 text-left">CHEST (IN)</th>
                      <th className="p-2 text-left">WAIST (IN)</th>
                      <th className="p-2 text-left">LENGTH (IN)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SIZE_GUIDE.map((r) => (
                      <tr key={r[0]} className="border-b border-white/5">
                        {r.map((c) => (
                          <td key={c} className="p-2">
                            {c}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {product.sizes.map((s) => {
                const stock = product.inventory?.[s];
                const soldOut = stock !== undefined && stock !== null && stock <= 0;
                return (
                  <button
                    key={s}
                    data-testid={`size-option-${s.toLowerCase()}`}
                    disabled={soldOut}
                    onClick={() => {
                      setSize(s);
                      setSizeError(false);
                    }}
                    className={`min-w-[3rem] border px-3 py-2.5 font-mono text-xs tracking-widest transition-colors ${
                      soldOut
                        ? "cursor-not-allowed border-white/10 text-bone/20 line-through"
                        : size === s
                          ? "border-bone bg-bone text-ink"
                          : "border-white/20 text-bone/70 hover:border-white/50"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            {size && product.inventory?.[size] > 0 && product.inventory[size] <= 3 && (
              <p data-testid="low-stock-hint" className="mt-2 font-mono text-[10px] tracking-[0.25em] text-bone/60">
                ONLY {product.inventory[size]} LEFT — NO COINCIDENCES
              </p>
            )}
          </div>

          <button
            data-testid="add-to-cart-button"
            onClick={addToCart}
            disabled={!!(product.drop_at && new Date(product.drop_at) > new Date())}
            className={`mt-10 w-full py-5 font-display text-sm font-extrabold uppercase tracking-[0.25em] transition-opacity ${
              product.drop_at && new Date(product.drop_at) > new Date()
                ? "cursor-not-allowed border border-white/15 bg-transparent text-fade"
                : "bg-bone text-ink hover:opacity-90"
            }`}
          >
            {product.drop_at && new Date(product.drop_at) > new Date() ? "Locked until the drop" : `Add to cart — ${fmt(product.price)}`}
          </button>
          <p className="mt-3 text-center font-mono text-[9px] tracking-[0.25em] text-fade">FREE SHIPPING OVER $200 — SHIPS IN 3–5 DAYS</p>

          <div className="mt-10">
            <Accordion title="DESCRIPTION" testId="accordion-description">
              {product.description}
            </Accordion>
            <Accordion title="FABRIC / MATERIAL" testId="accordion-fabric">
              {product.fabric}
            </Accordion>
            <Accordion title="FIT INFORMATION" testId="accordion-fit">
              {product.fit}
            </Accordion>
            <Accordion title="GARMENT DETAILS" testId="accordion-details">
              <ul className="list-none space-y-2">
                {product.details.map((d) => (
                  <li key={d} className="flex gap-3">
                    <span className="text-bone/40">+</span>
                    {d}
                  </li>
                ))}
              </ul>
            </Accordion>
            <Accordion title="SHIPPING" testId="accordion-shipping">
              {product.shipping}
            </Accordion>
          </div>
        </div>
      </div>

      <section data-testid="the-meaning-section" className="relative overflow-hidden border-t border-white/10 px-5 py-24 sm:px-8">
        <span className="pointer-events-none absolute -left-8 bottom-0 select-none font-display text-[15rem] font-extrabold leading-none text-bone/[0.04]">{product.angel}</span>
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.35em] text-fade">THE MEANING — {product.angel}</p>
            <p className="mt-6 font-display text-2xl font-light leading-snug tracking-tight text-neutral-200 sm:text-4xl">“{product.meaning}”</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
